import React, { useEffect, useState } from "react";
import { ShieldAlert, Phone, X, Plus, Check, Hospital as HospitalIcon } from "lucide-react";
import { Card, Input, Textarea, Toggle, RepeatableGroup, PlaceSearch } from "./FormComponents";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapViewUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
            if (center[0] && center[1]) {
                map.flyTo(center, 17, { duration: 1.5 });
            }
        }, 100);
    }, [center, map]);
    return null;
};

// Haversine distance in meters
const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const getWalkingTime = (meters) => {
    const minutes = Math.round(meters / 80);
    if (minutes < 1) return 'Less than 1 min walk';
    if (minutes === 1) return '1 min walk';
    return `${minutes} min walk`;
};

if (typeof document !== 'undefined') {
    const styleId = 'map-filters-style';
    if (!document.getElementById(styleId)) {
        const s = document.createElement('style');
        s.id = styleId;
        s.textContent = `
            .dark-map-tiles .leaflet-tile-container { filter: brightness(1.6) contrast(1.2); }
            .light-map-tiles .leaflet-tile-container { filter: grayscale(1) contrast(2.5) brightness(1.1); }
        `;
        document.head.appendChild(s);
    }
}

export const Step15 = ({ data, updateData, isLight, showValidation, propertyLocation }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newNumber, setNewNumber] = useState({ label: "", number: "", description: "" });

  const handleNestedChange = (parent, field, value) => {
    updateData("safetyMaintenance", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleEquipmentToggle = (key, field, value) => {
    const updates = { [field]: value };
    if (field === "available" && !value) {
      updates.location = "";
    }
    updateData("safetyMaintenance", {
      ...data,
      equipment: {
        ...data.equipment,
        [key]: {
          ...data.equipment[key],
          ...updates
        }
      }
    });
  };

  const handleSaveNewNumber = () => {
    if (newNumber.label && newNumber.number && newNumber.description) {
      handleNestedChange("emergency", "numbers", [...data.emergency.numbers, newNumber]);
      setNewNumber({ label: "", number: "", description: "" });
      setIsAdding(false);
    }
  };

  const handleHospitalUpdate = (index, field, value) => {
    const h = [...data.hospitals];
    h[index] = { ...h[index], [field]: value };
    updateData("safetyMaintenance", { ...data, hospitals: h });
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Emergency Contacts" icon={ShieldAlert} isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <h4 className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Emergency Numbers</h4>
            <button type="button" onClick={() => setIsAdding(true)} className="text-blue-500 font-black text-[10px] tracking-widest flex items-center gap-1.5 hover:text-blue-400 transition-all active:scale-95">
              <Plus size={14} /> ADD NEW
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.emergency.numbers.map((item, index) => (
              <div key={index} className={`relative p-5 border rounded-3xl group transition-all ${isLight ? 'bg-red-50/40 border-red-100 shadow-sm' : 'bg-red-500/5 border-red-500/10'}`}>
                <button 
                  type="button" 
                  onClick={() => handleNestedChange("emergency", "numbers", data.emergency.numbers.filter((_, i) => i !== index))}
                  className={`absolute top-4 right-4 p-1.5 rounded-lg transition-all opacity-0 group-hover:opacity-100 ${isLight ? "text-red-400 hover:bg-red-50" : "text-red-500/50 hover:bg-red-500/10 hover:text-red-400"}`}
                >
                  <X size={14} />
                </button>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg font-black text-red-500">{item.number}</span>
                  <span className={`text-sm font-bold ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>– {item.label}</span>
                </div>
                <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.description}</p>
              </div>
            ))}
          </div>

          {isAdding && (
            <div className={`p-6 border-2 border-dashed rounded-xl flex flex-col gap-4 ${isLight ? 'border-neutral-200' : 'border-white/10'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Service Name" required placeholder="Local Guard" value={newNumber.label} isLight={isLight} showValidation={showValidation} onChange={(e) => setNewNumber({ ...newNumber, label: e.target.value })} />
                <Input label="Number" required placeholder="112" value={newNumber.number} isLight={isLight} showValidation={showValidation} onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setNewNumber({ ...newNumber, number: val });
                }} />
              </div>
              <Textarea label="Short Description" required placeholder="What is this number for?" value={newNumber.description} isLight={isLight} showValidation={showValidation} onChange={(e) => setNewNumber({ ...newNumber, description: e.target.value })} />
              <div className="flex gap-3">
                <button type="button" onClick={handleSaveNewNumber} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-500 transition-colors">Save Contact</button>
                <button type="button" onClick={() => setIsAdding(false)} className={`flex-1 py-2 rounded-lg font-bold ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-white/5 text-neutral-300'}`}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Near Hospitals" icon={HospitalIcon} subtitle="Nearby hospitals and medical services for guests." isLight={isLight}>
        <RepeatableGroup 
          label="Hospitals" items={data.hospitals} isLight={isLight}
          onAdd={() => updateData("safetyMaintenance", { ...data, hospitals: [...data.hospitals, { id: Date.now().toString(), name: "", address: "", hours: "", distance: "", lat: null, lon: null, notes: "" }] })}
          onRemove={(index) => updateData("safetyMaintenance", { ...data, hospitals: data.hospitals.filter((_, i) => i !== index) })}
          renderItem={(item, index) => (
            <div className="flex flex-col gap-6">
              <PlaceSearch 
                label="Search Hospital" isLight={isLight} value={item.name}
                locationBias={propertyLocation}
                onSelect={(place) => {
                  let distance = item.distance;
                  if (propertyLocation?.lat && propertyLocation?.lon && place.lat && place.lon) {
                    const meters = haversineDistance(propertyLocation.lat, propertyLocation.lon, place.lat, place.lon);
                    distance = getWalkingTime(meters);
                  }
                  const newHospitals = [...data.hospitals];
                  newHospitals[index] = { 
                    ...newHospitals[index],
                    name: place.name, 
                    address: place.address, 
                    lat: place.lat, 
                    lon: place.lon,
                    hours: place.hours || "",
                    distance: distance
                  };
                  updateData("safetyMaintenance", { ...data, hospitals: newHospitals });
                }}
              />

              <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                <MapContainer 
                  center={[item.lat || 37.9838, item.lon || 23.7275]} 
                  zoom={item.lat ? 17 : 13} 
                  scrollWheelZoom={true} 
                  style={{ height: '100%', width: '100%' }}
                  className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                >
                  {isLight ? (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  ) : (
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                      minZoom={0} maxZoom={20}
                    />
                  )}
                  {item.lat && item.lon && <Marker position={[item.lat, item.lon]} />}
                  <MapViewUpdater center={[item.lat || 37.9838, item.lon || 23.7275]} />
                </MapContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Hospital Name" value={item.name} isLight={isLight} onChange={(e) => handleHospitalUpdate(index, "name", e.target.value)} />
                <Input label="Address" value={item.address} isLight={isLight} onChange={(e) => handleHospitalUpdate(index, "address", e.target.value)} />
                <Input label="Business Hours" placeholder="24/7" value={item.hours} isLight={isLight} onChange={(e) => handleHospitalUpdate(index, "hours", e.target.value)} />
                <div className="flex flex-col gap-1">
                  <Input label="Distance from property" value={item.distance} isLight={isLight} onChange={(e) => handleHospitalUpdate(index, "distance", e.target.value)} />
                  {item.distance && (
                    <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Auto-calculated from property location
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Input label="Additional info" value={item.notes} isLight={isLight} onChange={(e) => handleHospitalUpdate(index, "notes", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        />
      </Card>

      <Card title="Safety Equipment" icon={ShieldAlert} subtitle="Safety equipment available for guest use." isLight={isLight}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {Object.keys(data.equipment).map((key) => (
            <Toggle 
              key={key}
              label={key === 'cctv' ? 'CCTV on property.' : key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              value={data.equipment[key].available} isLight={isLight}
              onChange={(val) => handleEquipmentToggle(key, "available", val)}
            >
              {key !== 'smokeDetector' && key !== 'carbonMonoxide' && (
                <Input 
                  label={key === 'cctv' ? 'Camera locations' : "Location"} required 
                  placeholder={key === 'cctv' ? 'Entrance door, balcony' : ''}
                  value={data.equipment[key].location} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(e) => handleEquipmentToggle(key, "location", e.target.value)}
                />
              )}
            </Toggle>
          ))}
        </div>
      </Card>
    </div>
  );
};
